/**
 * 出站 Webhook 配置（仅商户）。
 *
 * 签名密钥是从平台根密钥按 `merchant_org_id` 单向派生的，一个商户一把。
 * 根密钥永远不出进程，所以**这里是商户拿到自己那把密钥的唯一途径** ——
 * 没有它，商户收到的每一条回调都验不过签，只能被迫关掉验签（等于把伪造回调的门敞开）。
 *
 * 密钥**不提供轮换**：它是派生的，除了换根密钥（影响全平台每个商户）之外没有东西可以只换这一把。
 * 提供一个看起来能轮换、实际什么也没换的按钮，比不提供更糟。
 */

import { useState } from 'react';
import { request } from '../lib/api.ts';
import { useAsync, useMutation } from '../lib/hooks.ts';
import { useSession } from '../lib/session.tsx';
import { Button, Card, ErrorBox, Field, Loading, TextInput } from '../components/ui.tsx';
import { formatDateTime } from '../lib/format.ts';
import type { WebhookConfig } from '../lib/types.ts';

export function WebhookPage() {
  const session = useSession();
  const orgId = session.me.org.id;
  const config = useAsync<WebhookConfig>(`/v1/orgs/${orgId}/webhook`);
  const [notifyUrl, setNotifyUrl] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);

  const save = useMutation(async (value: string | null) =>
    await request<WebhookConfig>(`/v1/orgs/${orgId}/webhook`, {
      method: 'PATCH',
      body: { notify_url: value },
    }));

  if (config.error !== null) {
    return (
      <>
        <h1>Webhook</h1>
        <ErrorBox error={config.error} />
      </>
    );
  }
  if (config.data === null) return <><h1>Webhook</h1><Loading /></>;

  const data = config.data;
  const current = notifyUrl ?? data.notify_url ?? '';

  return (
    <>
      <h1>Webhook</h1>

      <Card
        title="默认回调地址"
        sub="只在建单那一刻被写进该笔支付；改这里不会让历史支付的通知改道"
      >
        <div className="stack">
          <ErrorBox error={save.error} />
          <Field
            label="通知地址"
            hint="必须以 http:// 或 https:// 开头。单笔请求里显式传的 notify_url 永远优先于这里。"
          >
            <TextInput
              value={current}
              onChange={setNotifyUrl}
              placeholder="https://your-app.example.com/webhooks/bipay"
            />
          </Field>
          <div className="row">
            <Button
              variant="primary"
              disabled={save.pending || current === (data.notify_url ?? '')}
              onClick={() => {
                void save.run(current === '' ? null : current).then((result) => {
                  if (result !== null) { setNotifyUrl(null); config.reload(); }
                });
              }}
            >
              {save.pending ? '保存中…' : '保存'}
            </Button>
            {data.notify_url !== null && (
              <Button
                disabled={save.pending}
                onClick={() => {
                  void save.run(null).then((result) => {
                    if (result !== null) { setNotifyUrl(null); config.reload(); }
                  });
                }}
              >
                清空
              </Button>
            )}
            <span className="card-sub">最后更新 {formatDateTime(data.updated_at)}</span>
          </div>
        </div>
      </Card>

      <Card title="验签密钥" sub="一个商户一把，由平台根密钥单向派生">
        <div className="stack">
          <div className="alert" data-tone="warn">
            <span className="alert-title">这把密钥不能轮换</span>
            <span>
              它是派生出来的 —— 除了更换平台根密钥（会影响全平台每个商户）之外，
              没有东西可以只换这一把。请像对待其它长期密钥一样保管它。
              <strong>持有它的人可以伪造任何发给你的事件。</strong>
            </span>
          </div>

          <Field label="Signing Secret">
            {revealed ? (
              <div className="secret-box">{data.signing_secret}</div>
            ) : (
              <Button onClick={() => { setRevealed(true); }}>点击显示密钥</Button>
            )}
          </Field>
        </div>
      </Card>

      <Card title="怎么验签" sub="照这个做，不要自己发明">
        <div className="detail-grid">
          <span className="detail-key">签名头</span>
          <span className="detail-val mono">{data.signature_header}</span>

          <span className="detail-key">时间戳头</span>
          <span className="detail-val mono">{data.timestamp_header}</span>

          <span className="detail-key">算法</span>
          <span className="detail-val mono">{data.signature_algorithm}</span>

          <span className="detail-key">被签内容</span>
          <span className="detail-val mono">{data.signed_payload_format}</span>

          <span className="detail-key">签名头取值格式</span>
          <span className="detail-val mono">{data.signature_value_format}</span>

          <span className="detail-key">时间戳容差</span>
          <span className="detail-val mono">{data.timestamp_tolerance_seconds} 秒</span>

          <span className="detail-key">去重头</span>
          <span className="detail-val mono">{data.notification_id_header}</span>
        </div>

        <div className="stack" style={{ marginTop: 14 }}>
          <div className="alert" data-tone="info">
            <span className="alert-title">被签的是「时间戳 + 原始请求体」，不是请求体本身</span>
            <span>时间戳进签名才防得住重放。校验时请先比对时间戳是否在容差内，再比对 HMAC。</span>
          </div>

          <div className="alert" data-tone="info">
            <span className="alert-title">通知是 at-least-once，必须按 {data.notification_id_header} 去重</span>
            <span>
              同一个事件可能被投递多次（重试、网络抖动）。用这个头做幂等，
              否则一次重投可能让你的系统重复发货。
            </span>
          </div>

          <Field label="Node.js 验签示例">
            <div className="secret-box">
              {`import { createHmac, timingSafeEqual } from 'node:crypto';

function verify(rawBody, header, secret) {
  // header 形如 t=1730000000,v1=abcdef...
  const parts = Object.fromEntries(
    header.split(',').map((kv) => kv.split('=')),
  );
  const age = Math.abs(Date.now() / 1000 - Number(parts.t));
  if (age > ${data.timestamp_tolerance_seconds}) return false;   // 超出容差，按重放丢弃

  const expected = createHmac('sha256', secret)
    .update(\`\${parts.t}.\${rawBody}\`)   // 注意是「时间戳.原始体」
    .digest('hex');

  const a = Buffer.from(expected, 'hex');
  const b = Buffer.from(parts.v1, 'hex');
  return a.length === b.length && timingSafeEqual(a, b);
}`}
            </div>
          </Field>
        </div>
      </Card>
    </>
  );
}
